import React from "react";
import { Link, useLocation } from "react-router-dom";
import clsx from "clsx";

import {
  homePath,
  questionnairePath,
  myDataPath,
  selectMaterialsPath,
  listMaterialPath,
  listActiveLearningPath,
  assessmentDescriptionPath,
  assessmentPath,
  defaultPath,
  actionListPath,
} from "../../../studentStore";
import { isAvailablePartItemCodes } from "../../../libs/ActiveLearning";
import LogoImg from "../../../assets/images/logo.png";
import { ReactComponent as HomeIcon } from "../../../assets/svg/home-menu.svg";
import { ReactComponent as CategoryIcon } from "../../../assets/svg/category.svg";
import { ReactComponent as LogoutIcon } from "../../../assets/svg/logout.svg";
import { ReactComponent as QuestionnaireIcon } from "../../../assets/svg/questionnaire.svg";
import { ReactComponent as MyDataIcon } from "../../../assets/svg/class-data.svg";
import { ReactComponent as AssessmentIcon } from "../../../assets/svg/assessment.svg";
import { ReactComponent as GearIcon } from "../../../assets/svg/gear.svg";
import { ReactComponent as ActionListIcon } from "../../../assets/svg/action_list.svg";
import Modal from "../../../atoms/Modal";
import { STUDENT_SESSION_KEY } from "../../../Definition";

import styles from "./Sidebar.module.scss";

export interface SidebarProps {
  className?: string;
  isLoaded: boolean;
  isDlpEnabled: boolean;
  isSurveyEnabled: boolean;
  isOnlyAssessment: boolean;
  isG4sEnabled: boolean;
  isDummy: boolean;
  hasFadeInLeftAnimation?: boolean;
  availableItemCodes: string[];
  logout: () => void;
  isOnlyBase: boolean;
  isMasqueradeByTeacher: boolean;
}

const Sidebar: React.FC<SidebarProps> = props => {
  const [showModal, setShowModal] = React.useState(false);
  const location = useLocation();
  const path = location.pathname;
  const checkActive = (...args: string[]) => {
    return clsx(args.some(compare => compare === path) && styles.active);
  };
  const extra = sessionStorage.getItem(STUDENT_SESSION_KEY)
    ? `?session_id=${sessionStorage.getItem(STUDENT_SESSION_KEY)}`
    : "";

  return (
    <>
      <nav
        className={clsx(
          styles.sidebar,
          props.hasFadeInLeftAnimation && styles.fadeInLeftAnimation,
          props.className,
        )}
      >
        <a className={styles.logo} href={defaultPath}>
          <img src={LogoImg} alt="Logo" />
        </a>
        {props.isLoaded && (
          <ul className={styles.menuList}>
            {!props.isOnlyAssessment && (
              <>
                <li className={checkActive(homePath)}>
                  <Link aria-current="page" to={homePath}>
                    <HomeIcon />
                    HOME
                  </Link>
                </li>
                <li
                  className={clsx(
                    checkActive(
                      selectMaterialsPath,
                      listMaterialPath,
                      listActiveLearningPath,
                    ),
                    !props.isDlpEnabled && styles.disable,
                  )}
                >
                  <Link
                    aria-current="page"
                    to={
                      isAvailablePartItemCodes(props.availableItemCodes)
                        ? selectMaterialsPath
                        : listMaterialPath
                    }
                  >
                    <CategoryIcon />
                    教材一覧
                  </Link>
                </li>
                {!props.isDummy && (
                  <li
                    className={clsx(
                      `${checkActive(myDataPath)} ${
                        path.includes(myDataPath) && styles.active
                      }`,
                      !props.isDlpEnabled && styles.disable,
                    )}
                  >
                    <Link aria-current="page" to={myDataPath}>
                      <MyDataIcon />
                      マイデータ
                    </Link>
                  </li>
                )}
              </>
            )}
            {!props.isMasqueradeByTeacher && (
              <li
                className={checkActive(
                  assessmentDescriptionPath,
                  assessmentPath,
                )}
              >
                {props.isG4sEnabled ? (
                  <a
                    href={`${process.env.REACT_APP_STUDENT_GEAR_URL}/student/assessment_description${extra}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <AssessmentIcon />
                    自己評価ツール
                  </a>
                ) : (
                  <Link aria-current="page" to={assessmentDescriptionPath}>
                    <AssessmentIcon />
                    自己評価ツール
                  </Link>
                )}
              </li>
            )}
            {!props.isOnlyAssessment && !props.isOnlyBase && (
              <li className={checkActive(actionListPath)}>
                <Link aria-current="page" to={actionListPath}>
                  <ActionListIcon />
                  行動リスト
                </Link>
              </li>
            )}
            {props.isSurveyEnabled && !props.isOnlyAssessment && (
              <li
                className={clsx(
                  checkActive(questionnairePath),
                  !props.isDlpEnabled && styles.disable,
                )}
              >
                <Link aria-current="page" to={questionnairePath}>
                  <QuestionnaireIcon />
                  アンケート
                </Link>
              </li>
            )}
            <div className="mt-auto" />
            {props.isG4sEnabled && !props.isMasqueradeByTeacher && (
              <li className={styles.gearButton}>
                <a
                  href={`${process.env.REACT_APP_STUDENT_GEAR_URL}${extra}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <GearIcon />
                </a>
              </li>
            )}
            <li>
              <button onClick={() => setShowModal(true)}>
                <LogoutIcon />
                ログアウト
              </button>
            </li>
          </ul>
        )}
      </nav>
      {showModal && (
        <Modal
          onClose={() => setShowModal(false)}
          className={styles.modalLogout}
        >
          <div className={styles.modalLogout__title}>ログアウトしますか？</div>
          <div className={styles.modalLogout__actions}>
            <button
              className={styles.btnCancel}
              onClick={() => setShowModal(false)}
            >
              キャンセル
            </button>
            <button className={styles.btnOk} onClick={props.logout}>
              ログアウト
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Sidebar;
